import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Pagination,
  Tooltip,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Avatar,
  Skeleton,
} from "@mui/material";
import {
  CardGiftcardOutlined as GiftCardIcon,
  Refresh as RefreshIcon,
  FileDownloadOutlined as DownloadIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  OpenInNew as ExternalLinkIcon,
  CheckCircleOutline as ActiveIcon,
  CancelOutlined as ExpiredIcon,
  TaskAlt as RedeemedIcon,
  VolunteerActivismOutlined as DonationIcon,
  ShareOutlined as ShareIcon,
  MonetizationOnOutlined as TokenIcon,
  Search as SearchIcon,
  RestartAlt as ResetIcon,
  AccountCircleOutlined as UserIcon,
  CalendarTodayOutlined as CalendarIcon,
  LockResetOutlined as WithdrawIcon,
  ConfirmationNumberOutlined as VoucherIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import axios from "axios";
import * as XLSX from "xlsx";

// Web3 & Services
import cinstance from "../../../Service/randomcinstance";
import usercinstance from "../../../Service/usercinstance";
import giftcardcinstance from "../../../Service/giftcardcinstance";
import web3 from "../../../Service/web3";
import getImageUrlByHash from "../../../../static/logoUtils";
import AutobetLogo from "../../../../static/Images/logo.png";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import { Path, getNetworkCurrency } from "../../../../static/Constants";

// Layout & Dashboard Common Components
import { DashboardLayout } from "../layout";
import { MetricCard } from "../metrics";
import {
  CommonButton,
  CommonChip,
  CommonInput,
  CommonSelect,
  CommonTable,
  CommonPageHeader,
  CommonTabs,
  CommonTab,
  CommonViewToggle,
  CommonDialog,
  CommonMenu,
  AccessDeniedScreen,
} from "../../ui";

const withTimeout = (promise, ms = 4000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), ms),
    ),
  ]);
};

const safeFromWei = (val, unit = "ether") => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, unit);
  } catch (err) {
    try {
      const num = Number(val);
      if (isNaN(num)) return "0";
      return (num / 1e18).toFixed(4);
    } catch {
      return "0";
    }
  }
};

const shortenAddress = (addr) => {
  if (!addr) return "";
  if (addr.length <= 10) return addr;
  return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
};

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "bet_high", label: "Highest BET Value" },
  { value: "bet_low", label: "Lowest BET Value" },
  { value: "price_high", label: "Price (High to Low)" },
  { value: "price_low", label: "Price (Low to High)" },
  { value: "exp_soon", label: "Expiration (Soonest)" },
];

const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "standard", label: "Standard Sale" },
  { value: "donation", label: "Charity Donation" },
];

const PAGE_SIZE_OPTIONS = [
  { value: 6, label: "6 per page" },
  { value: 12, label: "12 per page" },
  { value: 24, label: "24 per page" },
  { value: 48, label: "48 per page" },
];

/**
 * Realistic Gift Card Skeleton Card matching exact card layout & tokens
 */
const GiftCardSkeletonCard = () => (
  <Card
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: borderRadiusPx.xl,
      boxShadow: customShadows.card,
      border: `1px solid ${colors.slate[200]}`,
      overflow: "hidden",
      bgcolor: colors.background.paper,
    }}
  >
    {/* Media Banner Skeleton */}
    <Box
      sx={{
        position: "relative",
        height: 160,
        bgcolor: colors.slate[100],
        overflow: "hidden",
      }}
    >
      <Skeleton
        variant="rectangular"
        width="100%"
        height="100%"
        animation="wave"
        sx={{ bgcolor: colors.slate[200] }}
      />
      <Box sx={{ position: "absolute", top: 12, left: 12 }}>
        <Skeleton
          variant="rounded"
          width={72}
          height={24}
          animation="wave"
          sx={{
            borderRadius: borderRadiusPx.full,
            bgcolor: "rgba(255, 255, 255, 0.85)",
          }}
        />
      </Box>
      <Box sx={{ position: "absolute", top: 12, right: 12 }}>
        <Skeleton
          variant="rounded"
          width={64}
          height={24}
          animation="wave"
          sx={{
            borderRadius: borderRadiusPx.full,
            bgcolor: "rgba(255, 255, 255, 0.85)",
          }}
        />
      </Box>
      <Box sx={{ position: "absolute", bottom: 10, right: 12 }}>
        <Skeleton
          variant="rounded"
          width={64}
          height={22}
          animation="wave"
          sx={{
            borderRadius: borderRadiusPx.sm,
            bgcolor: "rgba(15, 23, 42, 0.45)",
          }}
        />
      </Box>
    </Box>

    {/* Content Body Skeleton */}
    <CardContent
      sx={{
        p: 2.5,
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Skeleton
        variant="text"
        width="65%"
        height={28}
        animation="wave"
        sx={{ mb: 0.75, borderRadius: borderRadiusPx.sm }}
      />
      <Skeleton
        variant="text"
        width="95%"
        height={18}
        animation="wave"
        sx={{ mb: 0.25 }}
      />
      <Skeleton
        variant="text"
        width="70%"
        height={18}
        animation="wave"
        sx={{ mb: 2 }}
      />

      <Box
        sx={{
          p: 1.5,
          mb: 2,
          bgcolor: colors.slate[50],
          borderRadius: borderRadiusPx.lg,
          border: `1px solid ${colors.slate[200]}`,
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Skeleton
              variant="text"
              width={36}
              height={14}
              animation="wave"
              sx={{ mb: 0.25 }}
            />
            <Skeleton variant="text" width={84} height={24} animation="wave" />
          </Grid>
          <Grid item xs={6}>
            <Skeleton
              variant="text"
              width={36}
              height={14}
              animation="wave"
              sx={{ mb: 0.25 }}
            />
            <Skeleton variant="text" width={72} height={24} animation="wave" />
          </Grid>
        </Grid>
      </Box>

      <Stack spacing={1.2} sx={{ mb: 2.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Skeleton variant="text" width="35%" height={16} animation="wave" />
          <Skeleton variant="text" width="40%" height={16} animation="wave" />
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Skeleton variant="text" width="30%" height={16} animation="wave" />
          <Skeleton variant="text" width="45%" height={16} animation="wave" />
        </Stack>
      </Stack>

      <Box
        sx={{
          mt: "auto",
          pt: 1.5,
          borderTop: `1px solid ${colors.slate[100]}`,
        }}
      >
        <Stack direction="row" spacing={1}>
          <Skeleton
            variant="rounded"
            width="50%"
            height={34}
            animation="wave"
            sx={{ borderRadius: borderRadiusPx.md }}
          />
          <Skeleton
            variant="rounded"
            width="50%"
            height={34}
            animation="wave"
            sx={{ borderRadius: borderRadiusPx.md }}
          />
        </Stack>
      </Box>
    </CardContent>
  </Card>
);

/**
 * Unified GiftCardListPage Component
 * Supports both Admin and Creator views dynamically based on role.
 */
const GiftCardListPage = ({ role }) => {
  const history = useHistory();
  const location = useLocation();

  // Determine active role: 'admin' | 'creator'
  const resolvedRole =
    role || (location.pathname.startsWith("/admin") ? "admin" : "creator");
  const isAdmin = resolvedRole === "admin";

  // Authentication & Web3 State
  const [account, setAccount] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [creatorInfo, setCreatorInfo] = useState({
    name: "Creator",
    active: true,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data State
  const [giftcardList, setGiftCardList] = useState([]);
  const [giftcardLogoData, setGiftcardLogoData] = useState([]);
  const [copiedKey, setCopiedKey] = useState(null);
  const [networkId, setNetworkId] = useState(null);

  useEffect(() => {
    const fetchNetId = async () => {
      try {
        if (web3 && web3.eth) {
          const id = await web3.eth.net.getId();
          setNetworkId(id);
        }
      } catch (e) {
        console.error("Error fetching network ID:", e);
      }
    };
    fetchNetId();
  }, []);

  const currency = useMemo(() => getNetworkCurrency(networkId), [networkId]);

  // Filters & Controls State
  const [activeTab, setActiveTab] = useState("all"); // "all" | "active" | "sold" | "expired" | "donation"
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "table"
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  // Withdrawal Dialog State
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  // Table Action Menu State
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuRow, setActionMenuRow] = useState(null);

  const handleOpenActionMenu = (event, row) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuRow(row);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuRow(null);
  };

  const apiUrl = process.env.REACT_APP_API_KEY;
  const currentDT = moment().unix();

  /**
   * Fetch Logos from Backend API
   */
  useEffect(() => {
    if (!apiUrl) return;
    axios
      .get(`${apiUrl}/giftcardlogos/getAllGiftcardLogos`)
      .then((response) => {
        if (response?.data?.result) {
          setGiftcardLogoData(response.data.result);
        }
      })
      .catch((err) => {
        console.warn("Could not fetch giftcard logos:", err);
      });
  }, [apiUrl]);

  /**
   * Fetch Gift Cards from Contract
   */
  const fetchGiftCards = useCallback(async () => {
    try {
      if (!window.ethereum && !web3) {
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      window.web3 = web3;
      let accounts = [];
      try {
        if (window.ethereum?.request) {
          accounts = await window.ethereum.request({ method: "eth_accounts" });
        }
        if (!accounts || accounts.length === 0) {
          accounts = await web3.eth.getAccounts();
        }
      } catch (err) {
        console.warn("Account retrieval warning:", err);
      }

      const userAccount = accounts && accounts.length > 0 ? accounts[0] : "";
      setAccount(userAccount);

      if (!userAccount) {
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      if (isAdmin) {
        // Verify Admin permissions
        let contractAdmin = "";
        try {
          if (cinstance?.methods?.admin) {
            contractAdmin = await withTimeout(
              cinstance.methods.admin().call(),
              3000,
            );
          }
        } catch (adminErr) {
          console.warn("Could not fetch contract admin:", adminErr);
        }

        const authorized = Boolean(
          contractAdmin &&
            userAccount &&
            contractAdmin.toLowerCase() === userAccount.toLowerCase(),
        );
        setIsAuthorized(authorized);
        setAuthChecked(true);

        if (!authorized) {
          setLoading(false);
          return;
        }
      } else {
        // Verify Creator Organization status
        try {
          const orgData = await usercinstance.methods
            .organisationbyaddr(userAccount)
            .call();
          setCreatorInfo(orgData || { name: "Creator", active: true });
          setIsAuthorized(Boolean(orgData ? orgData.active : true));
        } catch (orgErr) {
          console.warn("Could not fetch creator org info", orgErr);
          setCreatorInfo({ name: "Creator", active: true });
          setIsAuthorized(true);
        }
        setAuthChecked(true);
      }

      // Fetch Total Gift Card Count
      if (giftcardcinstance?.methods?.giftCardId) {
        const totalCountRes = await withTimeout(
          giftcardcinstance.methods.giftCardId().call(),
          4000,
        );
        const count = Number(totalCountRes);

        if (count < 0 || isNaN(count)) {
          setGiftCardList([]);
          setLoading(false);
          return;
        }

        // Batch load all gift cards
        const ids = [];
        for (let i = 0; i <= count; i++) {
          ids.push(i);
        }

        const batchSize = 10;
        const allFetchedCards = [];

        for (let i = 0; i < ids.length; i += batchSize) {
          const slice = ids.slice(i, i + batchSize);
          const promises = slice.map((id) =>
            withTimeout(
              giftcardcinstance.methods.giftCards(id).call(),
              3500,
            ).catch(() => null),
          );
          const results = await Promise.allSettled(promises);
          results.forEach((res) => {
            if (res.status === "fulfilled" && res.value && res.value.creator) {
              allFetchedCards.push(res.value);
            }
          });
        }

        // Role-based filtering: Creator sees only their created cards
        if (!isAdmin && userAccount) {
          const creatorCards = allFetchedCards.filter(
            (c) =>
              c.creator &&
              c.creator.toLowerCase() === userAccount.toLowerCase(),
          );
          setGiftCardList(creatorCards);
        } else {
          setGiftCardList(allFetchedCards);
        }
      }
    } catch (err) {
      console.error("Error fetching gift cards:", err);
      toast.error("Failed to load on-chain gift cards.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchGiftCards();

    // Listen to account switch
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          fetchGiftCards();
        } else {
          setAccount("");
        }
      };
      window.ethereum.on("accountsChanged", handleAccountsChanged);
      return () => {
        window.ethereum.removeListener(
          "accountsChanged",
          handleAccountsChanged,
        );
      };
    }
  }, [fetchGiftCards]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchGiftCards();
  };

  /**
   * Copy to Clipboard Helper
   */
  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
    toast.success("Copied to clipboard!");
  };

  /**
   * Share Gift Card Link
   */
  const handleShareLink = (item) => {
    let sharedUrl = `${window.location.origin}/giftcard/${item.giftCardId}`;
    if (item.voucherCode && item.voucherCode.trim() !== "") {
      sharedUrl += `?voucherCode=${encodeURIComponent(item.voucherCode)}`;
    }
    navigator.clipboard.writeText(sharedUrl);
    toast.success("Sharable Gift Card link copied!");
  };

  /**
   * Open Withdraw Dialog
   */
  const handleOpenWithdraw = (item) => {
    setWithdrawTarget(item);
    setWithdrawDialogOpen(true);
  };

  /**
   * Execute Expired Token Withdrawal
   */
  const handleConfirmWithdraw = async () => {
    if (!withdrawTarget || !account) return;
    try {
      setWithdrawing(true);
      await giftcardcinstance.methods
        .withdrawCreatorTokens(withdrawTarget.giftCardId)
        .send({ from: account });

      toast.success(
        "Expired tokens successfully withdrawn back to creator wallet!",
      );
      setWithdrawDialogOpen(false);
      setWithdrawTarget(null);
      await fetchGiftCards();
    } catch (err) {
      console.error("Token withdrawal failed:", err);
      toast.error(err?.message || "Withdrawal transaction failed.");
    } finally {
      setWithdrawing(false);
    }
  };

  /**
   * Export to Excel
   */
  const handleExportExcel = () => {
    if (giftcardList.length === 0) {
      toast.warning("No gift card records to export.");
      return;
    }

    const rows = filteredGiftCards.map((c) => {
      const isExpired = !c.isSold && Number(c.expDate) <= currentDT;
      const isWithdrawn = String(c.status) === "1";
      const statusLabel = c.isSold
        ? "Redeemed / Sold"
        : isWithdrawn
        ? "Expired (Tokens Withdrawn)"
        : isExpired
        ? "Expired (Unsold)"
        : "Active";

      return {
        "Card ID": c.giftCardId,
        "Token Name": c.tokenName || "AutoBET Pass",
        Type: c.isDonation ? "Donation" : "Standard Sale",
        "Token Value (BET)": safeFromWei(c.BETValue),
        [`Price (${currency})`]: safeFromWei(c.ETHPrice),
        Status: statusLabel,
        "Expiry Date": moment.unix(c.expDate).format("YYYY-MM-DD HH:mm:ss"),
        "Creator Address": c.creator,
        "Buyer Address": c.buyer || (c.isSold ? "Claimed" : "None"),
        "Voucher Code": c.voucherCode || "N/A",
        Description: c.Desc || "",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "GiftCards");
    XLSX.writeFile(
      workbook,
      `Autobet_GiftCards_${isAdmin ? "Admin" : "Creator"}_${moment().format(
        "YYYYMMDD_HHmmss",
      )}.xlsx`,
    );
    toast.success("Gift cards spreadsheet exported successfully!");
  };

  /**
   * Reset All Filters
   */
  const handleResetFilters = () => {
    setActiveTab("all");
    setSearchQuery("");
    setTypeFilter("all");
    setSortBy("newest");
    setCurrentPage(1);
  };

  /**
   * Key Metric Counts
   */
  const metrics = useMemo(() => {
    let active = 0;
    let sold = 0;
    let expired = 0;
    let donation = 0;
    let totalBetVolume = 0;

    giftcardList.forEach((item) => {
      const isExp = !item.isSold && Number(item.expDate) <= currentDT;
      const isAct = !item.isSold && Number(item.expDate) > currentDT;

      if (isAct) active += 1;
      if (item.isSold) sold += 1;
      if (isExp) expired += 1;
      if (item.isDonation) donation += 1;

      try {
        const val = Number(safeFromWei(item.BETValue));
        if (!isNaN(val)) totalBetVolume += val;
      } catch {
        // ignore
      }
    });

    return {
      total: giftcardList.length,
      active,
      sold,
      expired,
      donation,
      totalBetVolume: totalBetVolume.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      }),
    };
  }, [giftcardList, currentDT]);

  /**
   * Filtered & Sorted Gift Cards
   */
  const filteredGiftCards = useMemo(() => {
    return giftcardList
      .filter((item) => {
        const isExp = !item.isSold && Number(item.expDate) <= currentDT;
        const isAct = !item.isSold && Number(item.expDate) > currentDT;

        // 1. Tab filter
        if (activeTab === "active" && !isAct) return false;
        if (activeTab === "sold" && !item.isSold) return false;
        if (activeTab === "expired" && !isExp) return false;
        if (activeTab === "donation" && !item.isDonation) return false;

        // 2. Type dropdown filter
        if (typeFilter === "standard" && item.isDonation) return false;
        if (typeFilter === "donation" && !item.isDonation) return false;

        // 3. Search query
        if (searchQuery.trim() !== "") {
          const q = searchQuery.toLowerCase().trim();
          const matchId = String(item.giftCardId).toLowerCase().includes(q);
          const matchName = (item.tokenName || "").toLowerCase().includes(q);
          const matchDesc = (item.Desc || "").toLowerCase().includes(q);
          const matchCreator = (item.creator || "").toLowerCase().includes(q);
          const matchBuyer = (item.buyer || "").toLowerCase().includes(q);
          const matchVoucher = (item.voucherCode || "")
            .toLowerCase()
            .includes(q);

          if (
            !matchId &&
            !matchName &&
            !matchDesc &&
            !matchCreator &&
            !matchBuyer &&
            !matchVoucher
          ) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === "newest")
          return Number(b.giftCardId) - Number(a.giftCardId);
        if (sortBy === "oldest")
          return Number(a.giftCardId) - Number(b.giftCardId);
        if (sortBy === "bet_high")
          return (
            Number(safeFromWei(b.BETValue)) - Number(safeFromWei(a.BETValue))
          );
        if (sortBy === "bet_low")
          return (
            Number(safeFromWei(a.BETValue)) - Number(safeFromWei(b.BETValue))
          );
        if (sortBy === "price_high")
          return (
            Number(safeFromWei(b.ETHPrice)) - Number(safeFromWei(a.ETHPrice))
          );
        if (sortBy === "price_low")
          return (
            Number(safeFromWei(a.ETHPrice)) - Number(safeFromWei(b.ETHPrice))
          );
        if (sortBy === "exp_soon") return Number(a.expDate) - Number(b.expDate);
        return 0;
      });
  }, [giftcardList, activeTab, typeFilter, searchQuery, sortBy, currentDT]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredGiftCards.length / pageSize) || 1;
  const paginatedCards = useMemo(() => {
    const offset = (currentPage - 1) * pageSize;
    return filteredGiftCards.slice(offset, offset + pageSize);
  }, [filteredGiftCards, currentPage, pageSize]);

  // Adjust page if out of bounds after filtering
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const detailsBasePath = isAdmin
    ? Path.adminGiftCardDetailsBase
    : Path.orgGiftCardDetailsBase;

  /**
   * Table Columns Definition for CommonTable
   */
  const tableColumns = useMemo(() => {
    const cols = [
      {
        id: "card",
        label: "Card & Token",
        minWidth: 220,
        render: (_, row) => {
          const imgUrl =
            getImageByLogoHash(row?.logoHash, giftcardLogoData) || AutobetLogo;
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                src={imgUrl}
                alt={row?.tokenName || "Gift Card"}
                variant="rounded"
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: colors.slate[100],
                  border: `1px solid ${colors.slate[200]}`,
                  objectFit: "cover",
                }}
              >
                <GiftCardIcon sx={{ color: colors.primary.main }} />
              </Avatar>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: colors.text.primary }}
                >
                  {row?.tokenName || `Pass #${row?.giftCardId}`}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{ color: colors.text.secondary, fontWeight: 600 }}
                  >
                    ID: #{row?.giftCardId}
                  </Typography>
                  {row?.voucherCode && (
                    <CommonChip
                      label={`Voucher: ${row.voucherCode}`}
                      variant="neutral"
                      size="small"
                      sx={{ height: 18, fontSize: "0.68rem" }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          );
        },
      },
      {
        id: "type",
        label: "Type",
        minWidth: 120,
        render: (_, row) => (
          <CommonChip
            label={row?.isDonation ? "Donation" : "Standard"}
            variant={row?.isDonation ? "warning" : "primary"}
            size="small"
            icon={
              row?.isDonation ? (
                <DonationIcon sx={{ fontSize: 14 }} />
              ) : (
                <GiftCardIcon sx={{ fontSize: 14 }} />
              )
            }
          />
        ),
      },
      {
        id: "value",
        label: "Token Value",
        minWidth: 140,
        render: (_, row) => (
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: colors.primary.main }}
            >
              {safeFromWei(row?.BETValue)} BET
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.secondary }}>
              {row?.tokenName || "AutoBET"}
            </Typography>
          </Box>
        ),
      },
      {
        id: "price",
        label: `Price (${currency})`,
        minWidth: 120,
        render: (_, row) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: colors.amber[700] }}
          >
            {safeFromWei(row?.ETHPrice)} {currency}
          </Typography>
        ),
      },
      {
        id: "status",
        label: "Status",
        minWidth: 130,
        render: (_, row) => {
          const isExpired = !row?.isSold && Number(row?.expDate) <= currentDT;
          const isWithdrawn = String(row?.status) === "1";
          if (row?.isSold) {
            return (
              <CommonChip
                label="Redeemed"
                variant="neutral"
                size="small"
                icon={<RedeemedIcon sx={{ fontSize: 14 }} />}
              />
            );
          }
          if (isWithdrawn) {
            return (
              <CommonChip
                label="Withdrawn"
                variant="neutral"
                size="small"
                icon={<WithdrawIcon sx={{ fontSize: 14 }} />}
              />
            );
          }
          if (isExpired) {
            return (
              <CommonChip
                label="Expired"
                variant="danger"
                size="small"
                icon={<ExpiredIcon sx={{ fontSize: 14 }} />}
              />
            );
          }
          return (
            <CommonChip
              label="Active"
              variant="success"
              size="small"
              icon={<ActiveIcon sx={{ fontSize: 14 }} />}
            />
          );
        },
      },
      {
        id: "expiry",
        label: "Expiry Date",
        minWidth: 150,
        render: (_, row) => {
          const isExpired = !row?.isSold && Number(row?.expDate) <= currentDT;
          const expUnix = row?.expDate ? Number(row.expDate) : 0;
          return (
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: colors.text.primary }}
              >
                {expUnix
                  ? moment.unix(expUnix).format("DD MMM YYYY, HH:mm")
                  : "—"}
              </Typography>
              {expUnix > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: isExpired
                      ? colors.error.main
                      : colors.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  {isExpired
                    ? `Expired ${moment.unix(expUnix).fromNow()}`
                    : `Expires ${moment.unix(expUnix).fromNow()}`}
                </Typography>
              )}
            </Box>
          );
        },
      },
    ];

    if (isAdmin) {
      cols.push({
        id: "creator",
        label: "Creator",
        minWidth: 150,
        render: (_, row) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                fontWeight: 600,
                color: colors.text.secondary,
              }}
            >
              {shortenAddress(row?.creator)}
            </Typography>
            <Tooltip
              title={
                copiedKey === `creator-${row?.giftCardId}`
                  ? "Copied!"
                  : "Copy creator address"
              }
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(row?.creator, `creator-${row?.giftCardId}`);
                }}
                sx={{ p: 0.5, color: colors.slate[500] }}
              >
                {copiedKey === `creator-${row?.giftCardId}` ? (
                  <CheckIcon
                    sx={{ fontSize: 14, color: colors.success.main }}
                  />
                ) : (
                  <CopyIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      });
    }

    cols.push({
      id: "actions",
      label: "Actions",
      align: "right",
      minWidth: 110,
      render: (_, row) => {
        const isExpired = !row?.isSold && Number(row?.expDate) <= currentDT;
        const canWithdraw = isExpired && String(row?.status) === "0";
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 0.75,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {canWithdraw && (
              <Tooltip title="Withdraw Expired Tokens">
                <IconButton
                  size="small"
                  onClick={() => handleOpenWithdraw(row)}
                  sx={{
                    color: colors.error.main,
                    bgcolor: colors.red[50],
                    border: `1px solid ${colors.red[200]}`,
                    borderRadius: borderRadiusPx.sm,
                    "&:hover": {
                      bgcolor: colors.red[100],
                      color: colors.error.dark,
                    },
                  }}
                >
                  <WithdrawIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="More options">
              <IconButton
                size="small"
                onClick={(e) => handleOpenActionMenu(e, row)}
                sx={{
                  color: colors.slate[600],
                  bgcolor: colors.slate[50],
                  border: `1px solid ${colors.slate[200]}`,
                  borderRadius: borderRadiusPx.sm,
                  "&:hover": {
                    bgcolor: colors.slate[100],
                    color: colors.slate[900],
                    borderColor: colors.slate[300],
                  },
                }}
              >
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    });

    return cols;
  }, [isAdmin, giftcardLogoData, currentDT, copiedKey]);

  return (
    <DashboardLayout
      role={resolvedRole}
      title={isAdmin ? "Gift Cards Management" : "My Gift Cards"}
      subtitle={
        isAdmin
          ? "Audit, monitor, filter, and inspect decentralized gift cards, vouchers, and community donation passes."
          : "Manage, track, and inspect all decentralized gift cards deployed by your creator organisation."
      }
      breadcrumbs={[
        {
          label: "Dashboard",
          href: isAdmin ? Path.admin : Path.org,
        },
        { label: isAdmin ? "Gift Cards List" : "My Gift Cards" },
      ]}
      headerActions={
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
        >
          {!isAdmin && (
            <CommonButton
              variant="contained"
              onClick={() => history.push(Path.orgcreatecard)}
              startIcon={<AddIcon />}
            >
              Create Gift Card
            </CommonButton>
          )}
          <CommonButton
            variant="outline"
            startIcon={
              <RefreshIcon
                sx={{
                  animation: refreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            }
            onClick={handleRefresh}
            disabled={loading || refreshing}
          >
            Refresh
          </CommonButton>
          <CommonButton
            variant="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={giftcardList.length === 0}
          >
            Export Excel
          </CommonButton>
        </Stack>
      }
      creatorAddress={account}
      userName={isAdmin ? "AutoBET Admin" : creatorInfo?.name || "Creator"}
      userRole={isAdmin ? "Administrator" : "Creator"}
      authChecked={authChecked}
      isAuthorized={isAuthorized}
      accessDeniedProps={{
        title: isAdmin ? "Admin Access Required" : "Account Access Denied",
        message: isAdmin
          ? "You must connect with the verified AutoBET smart contract administrator wallet to access the decentralized Gift Cards manager."
          : "You must be a registered Creator or organization to view My Gift Cards.",
        actionText: isAdmin
          ? "Back to Admin Dashboard"
          : "Register as Organisation",
        actionPath: isAdmin ? Path.admin : Path.registerasorg,
      }}
    >
      {/* Key Metrics Summary Cards */}
      <Box>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Gift Cards"
              value={metrics.total}
              loading={loading}
              icon={<GiftCardIcon />}
              subtitle={
                isAdmin
                  ? "All on-chain generated passes"
                  : "Created by your dashboard"
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Active & Available"
              value={metrics.active}
              loading={loading}
              icon={<ActiveIcon />}
              subtitle="Available for purchase / claim"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Redeemed Passes"
              value={metrics.sold}
              loading={loading}
              icon={<RedeemedIcon />}
              subtitle="Claimed & delivered to players"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total BET Liquidity"
              value={`${metrics.totalBetVolume} BET`}
              loading={loading}
              icon={<TokenIcon />}
              subtitle={`${metrics.donation} charity donation passes`}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Unified Card Container for Tabs, View Toggle & Filter Controls */}
      <Box
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: colors.background.paper,
          borderRadius: borderRadiusPx.xl,
          boxShadow: customShadows.card,
          border: `1px solid ${
            colors.dashboard.cardBorder || colors.slate[200]
          }`,
        }}
      >
        {/* Navigation Filter Tabs & View Toggle */}
        <Box
          sx={{
            borderBottom: `1px solid ${
              colors.dashboard.divider || colors.slate[200]
            }`,
            pb: 2,
            mb: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <CommonTabs
            value={activeTab}
            onChange={(newVal) => {
              setActiveTab(newVal);
              setCurrentPage(1);
            }}
            ariaLabel="Gift card status filter tabs"
          >
            <CommonTab
              label="All Cards"
              value="all"
              count={metrics.total}
              chipVariant="neutral"
            />
            <CommonTab
              label="Active / Available"
              value="active"
              count={metrics.active}
              chipVariant="success"
            />
            <CommonTab
              label="Redeemed"
              value="sold"
              count={metrics.sold}
              chipVariant="neutral"
            />
            <CommonTab
              label="Expired"
              value="expired"
              count={metrics.expired}
              chipVariant="danger"
            />
            <CommonTab
              label="Charity Donations"
              value="donation"
              count={metrics.donation}
              chipVariant="warning"
            />
          </CommonTabs>

          <CommonViewToggle value={viewMode} onChange={setViewMode} />
        </Box>

        {/* Search & Filter Controls Bar */}
        <Grid container spacing={2} alignItems="center">
          {/* Search Bar */}
          <Grid item xs={12} sm={12} md={4} lg={4.5}>
            <CommonInput
              placeholder={
                isAdmin
                  ? "Search by name, ID, voucher, creator..."
                  : "Search by name, ID, voucher code..."
              }
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              startAdornment={
                <SearchIcon sx={{ color: colors.slate[400], mr: 0.5 }} />
              }
            />
          </Grid>

          {/* Type Filter */}
          <Grid item xs={6} sm={4} md={2.5} lg={2.5}>
            <CommonSelect
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              options={TYPE_FILTER_OPTIONS}
            />
          </Grid>

          {/* Sort Dropdown */}
          <Grid item xs={6} sm={4} md={2.5} lg={2.5}>
            <CommonSelect
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
              options={SORT_OPTIONS}
            />
          </Grid>

          {/* Rows Per Page Dropdown */}
          <Grid item xs={6} sm={2} md={1.5} lg={1.5}>
            <CommonSelect
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              options={PAGE_SIZE_OPTIONS}
            />
          </Grid>

          {/* Reset Filters */}
          <Grid item xs={6} sm={2} md={1} lg={1}>
            <Tooltip title="Reset filters">
              <CommonButton
                variant="cancel"
                fullWidth
                onClick={handleResetFilters}
                sx={{ height: 42 }}
              >
                <ResetIcon sx={{ fontSize: 18 }} />
              </CommonButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Loading Skeleton - Grid View */}
      {loading && viewMode === "grid" && (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <GiftCardSkeletonCard />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Loading Skeleton - Table View */}
      {loading && viewMode === "table" && (
        <Box
          sx={{
            bgcolor: colors.background.paper,
            borderRadius: borderRadiusPx.xl,
            boxShadow: customShadows.card,
            border: `1px solid ${colors.slate[200]}`,
            overflow: "hidden",
          }}
        >
          <CommonTable
            columns={tableColumns}
            rows={[]}
            loading={true}
            rowKey="giftCardId"
          />
        </Box>
      )}

      {/* Empty State */}
      {!loading && filteredGiftCards.length === 0 && (
        <Box
          sx={{
            py: 8,
            px: 3,
            textAlign: "center",
            bgcolor: colors.background.paper,
            borderRadius: borderRadiusPx.xl,
            boxShadow: customShadows.card,
            border: `1px solid ${colors.slate[200]}`,
          }}
        >
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: colors.blue[50],
              color: colors.primary.main,
              mx: "auto",
              mb: 2,
            }}
          >
            <GiftCardIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: colors.text.primary,
              mb: 0.5,
            }}
          >
            No Gift Cards Found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              maxWidth: 440,
              mx: "auto",
              mb: 2.5,
            }}
          >
            {searchQuery || typeFilter !== "all" || activeTab !== "all"
              ? "No gift card passes matched your current filter criteria. Try changing filters or resetting search."
              : isAdmin
              ? "No decentralized gift card passes have been created on the smart contract yet."
              : "You haven't deployed any decentralized gift cards yet."}
          </Typography>
          {searchQuery || typeFilter !== "all" || activeTab !== "all" ? (
            <CommonButton variant="outline" onClick={handleResetFilters}>
              Clear All Filters
            </CommonButton>
          ) : !isAdmin ? (
            <CommonButton
              variant="primary"
              onClick={() => history.push(Path.orgcreatecard)}
              startIcon={<AddIcon />}
            >
              Create Your First Gift Card
            </CommonButton>
          ) : null}
        </Box>
      )}

      {/* CONTENT VIEW 1: GRID VIEW */}
      {!loading && filteredGiftCards.length > 0 && viewMode === "grid" && (
        <Box>
          <Grid container spacing={3}>
            {paginatedCards.map((item) => {
              const imgUrl =
                getImageByLogoHash(item.logoHash, giftcardLogoData) ||
                AutobetLogo;
              const isExpired =
                !item.isSold && Number(item.expDate) <= currentDT;

              return (
                <Grid item xs={12} sm={6} md={4} key={item.giftCardId}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: borderRadiusPx.xl,
                      boxShadow: customShadows.card,
                      border: `1px solid ${colors.slate[200]}`,
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: customShadows.cardHover,
                      },
                    }}
                  >
                    {/* Card Media Banner */}
                    <Box
                      sx={{
                        position: "relative",
                        height: 160,
                        bgcolor: colors.slate[900],
                        overflow: "hidden",
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={imgUrl}
                        alt={item.tokenName || "Gift Card"}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          opacity: 0.9,
                        }}
                      />
                      {/* Overlay Badges */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 12,
                          left: 12,
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <CommonChip
                          label={item.isDonation ? "Donation" : "Standard"}
                          variant={item.isDonation ? "warning" : "primary"}
                          size="small"
                        />
                      </Box>
                      <Box
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                        }}
                      >
                        {item.isSold ? (
                          <CommonChip
                            label="Redeemed"
                            variant="neutral"
                            size="small"
                          />
                        ) : String(item.status) === "1" ? (
                          <CommonChip
                            label="Withdrawn"
                            variant="neutral"
                            size="small"
                          />
                        ) : isExpired ? (
                          <CommonChip
                            label="Expired"
                            variant="danger"
                            size="small"
                          />
                        ) : (
                          <CommonChip
                            label="Active"
                            variant="success"
                            size="small"
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 10,
                          right: 12,
                          bgcolor: "rgba(15, 23, 42, 0.75)",
                          backdropFilter: "blur(6px)",
                          px: 1.2,
                          py: 0.4,
                          borderRadius: borderRadiusPx.sm,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "#ffffff", fontWeight: 700 }}
                        >
                          Pass #{item.giftCardId}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Card Content Body */}
                    <CardContent
                      sx={{
                        p: 2.5,
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Title & Description */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: colors.text.primary,
                          mb: 0.5,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.tokenName || "AutoBET Gift Card"}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.secondary,
                          mb: 2,
                          fontSize: "0.85rem",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minHeight: 38,
                        }}
                      >
                        {item.Desc ||
                          "Decentralized gaming voucher pass redeemable on AutoBET platform."}
                      </Typography>

                      {/* Values & Price Ribbon */}
                      <Box
                        sx={{
                          p: 1.5,
                          mb: 2,
                          bgcolor: colors.slate[50],
                          borderRadius: borderRadiusPx.lg,
                          border: `1px solid ${colors.slate[200]}`,
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text.secondary,
                                fontWeight: 600,
                              }}
                            >
                              Value
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 800,
                                color: colors.primary.main,
                              }}
                            >
                              {safeFromWei(item.BETValue)} BET
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text.secondary,
                                fontWeight: 600,
                              }}
                            >
                              Price
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 800,
                                color: colors.amber[700],
                              }}
                            >
                              {safeFromWei(item.ETHPrice)} {currency}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Details List */}
                      <Stack spacing={1} sx={{ mb: 2.5, fontSize: "0.8rem" }}>
                        {/* Expiry */}
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <CalendarIcon sx={{ fontSize: 14 }} /> Expiration
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: isExpired
                                ? colors.error.main
                                : colors.text.primary,
                            }}
                          >
                            {moment
                              .unix(item.expDate)
                              .format("DD MMM YYYY, HH:mm")}
                          </Typography>
                        </Stack>

                        {/* Creator Info */}
                        {isAdmin ? (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text.secondary,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <UserIcon sx={{ fontSize: 14 }} /> Creator
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: "monospace",
                                  fontWeight: 600,
                                  color: colors.text.secondary,
                                }}
                              >
                                {shortenAddress(item.creator)}
                              </Typography>
                              <Tooltip
                                title={
                                  copiedKey ===
                                  `grid-creator-${item.giftCardId}`
                                    ? "Copied!"
                                    : "Copy Address"
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopy(
                                      item.creator,
                                      `grid-creator-${item.giftCardId}`,
                                    )
                                  }
                                  sx={{ p: 0.25 }}
                                >
                                  {copiedKey ===
                                  `grid-creator-${item.giftCardId}` ? (
                                    <CheckIcon
                                      sx={{
                                        fontSize: 13,
                                        color: colors.success.main,
                                      }}
                                    />
                                  ) : (
                                    <CopyIcon sx={{ fontSize: 13 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        ) : null}

                        {/* Voucher Code (if available) */}
                        {item.voucherCode && item.voucherCode.trim() !== "" && (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text.secondary,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <VoucherIcon sx={{ fontSize: 14 }} /> Voucher
                            </Typography>
                            <CommonChip
                              label={item.voucherCode}
                              variant="neutral"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                fontWeight: 700,
                              }}
                            />
                          </Stack>
                        )}
                      </Stack>

                      {/* Card Actions Footer */}
                      <Box
                        sx={{
                          mt: "auto",
                          pt: 1.5,
                          borderTop: `1px solid ${colors.slate[100]}`,
                        }}
                      >
                        <Stack direction="row" spacing={1}>
                          <CommonButton
                            variant="outline"
                            size="small"
                            fullWidth
                            onClick={() => handleShareLink(item)}
                            startIcon={<ShareIcon sx={{ fontSize: 14 }} />}
                          >
                            Share
                          </CommonButton>
                          <CommonButton
                            variant="primary"
                            size="small"
                            fullWidth
                            onClick={() =>
                              history.push(
                                `${detailsBasePath}/${item.giftCardId}`,
                              )
                            }
                            endIcon={<ExternalLinkIcon sx={{ fontSize: 14 }} />}
                          >
                            Details
                          </CommonButton>
                        </Stack>

                        {isExpired && String(item.status) === "0" && (
                          <Box sx={{ mt: 1 }}>
                            <CommonButton
                              variant="dangerOutlined"
                              size="small"
                              fullWidth
                              onClick={() => handleOpenWithdraw(item)}
                              startIcon={<WithdrawIcon sx={{ fontSize: 14 }} />}
                            >
                              Withdraw Expired Tokens
                            </CommonButton>
                          </Box>
                        )}

                        {String(item.status) === "1" && (
                          <Box sx={{ mt: 1 }}>
                            <CommonButton
                              variant="cancel"
                              size="small"
                              fullWidth
                              disabled
                              startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                            >
                              Tokens Withdrawn
                            </CommonButton>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* CONTENT VIEW 2: TABLE VIEW */}
      {!loading && filteredGiftCards.length > 0 && viewMode === "table" && (
        <Box
          sx={{
            bgcolor: colors.background.paper,
            borderRadius: borderRadiusPx.xl,
            boxShadow: customShadows.card,
            border: `1px solid ${colors.slate[200]}`,
            overflow: "hidden",
          }}
        >
          <CommonTable
            columns={tableColumns}
            rows={paginatedCards}
            rowKey="giftCardId"
            emptyMessage="No gift cards found matching current filters."
            onRowClick={(row) =>
              history.push(`${detailsBasePath}/${row.giftCardId}`)
            }
          />
        </Box>
      )}

      {/* Pagination Controls */}
      {!loading && filteredGiftCards.length > 0 && totalPages > 1 && (
        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Showing {(currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, filteredGiftCards.length)} of{" "}
            {filteredGiftCards.length} gift cards
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, val) => setCurrentPage(val)}
            color="primary"
            shape="rounded"
            size="medium"
          />
        </Box>
      )}

      {/* TABLE ROW ACTION MENU */}
      <CommonMenu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        minWidth={190}
        items={
          actionMenuRow
            ? [
                {
                  id: "details",
                  label: "View Details",
                  icon: <ExternalLinkIcon sx={{ fontSize: 16 }} />,
                  onClick: () => {
                    history.push(
                      `${detailsBasePath}/${actionMenuRow?.giftCardId}`,
                    );
                  },
                },
                {
                  id: "share",
                  label: "Share Card Link",
                  icon: <ShareIcon sx={{ fontSize: 16 }} />,
                  onClick: () => {
                    handleShareLink(actionMenuRow);
                  },
                },
                ...(isAdmin
                  ? [
                      {
                        id: "copy-creator",
                        label: "Copy Creator Address",
                        icon: <CopyIcon sx={{ fontSize: 16 }} />,
                        onClick: () => {
                          handleCopy(
                            actionMenuRow?.creator,
                            `creator-${actionMenuRow?.giftCardId}`,
                          );
                        },
                      },
                    ]
                  : []),
                ...(!actionMenuRow?.isSold &&
                Number(actionMenuRow?.expDate) <= currentDT &&
                actionMenuRow?.status === "0"
                  ? [
                      { divider: true },
                      {
                        id: "withdraw",
                        label: "Withdraw Tokens",
                        icon: (
                          <WithdrawIcon
                            sx={{ fontSize: 16, color: colors.error.main }}
                          />
                        ),
                        danger: true,
                        onClick: () => {
                          handleOpenWithdraw(actionMenuRow);
                        },
                      },
                    ]
                  : []),
              ]
            : []
        }
      />

      {/* WITHDRAW CONFIRMATION DIALOG */}
      <CommonDialog
        open={withdrawDialogOpen}
        onClose={() => {
          if (!withdrawing) {
            setWithdrawDialogOpen(false);
            setWithdrawTarget(null);
          }
        }}
        title="Withdraw Expired Tokens"
        subtitle="Return locked tokens from an expired and unsold gift card back to the creator wallet."
        maxWidth="sm"
        actions={
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            sx={{ width: "100%" }}
          >
            <CommonButton
              variant="cancel"
              onClick={() => {
                setWithdrawDialogOpen(false);
                setWithdrawTarget(null);
              }}
              disabled={withdrawing}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="dangerSolid"
              onClick={handleConfirmWithdraw}
              disabled={withdrawing}
              startIcon={<WithdrawIcon />}
            >
              {withdrawing ? "Withdrawing..." : "Confirm Withdrawal"}
            </CommonButton>
          </Stack>
        }
      >
        {withdrawTarget && (
          <Box sx={{ pt: 1 }}>
            <Box
              sx={{
                p: 2,
                mb: 2.5,
                bgcolor: colors.slate[50],
                borderRadius: borderRadiusPx.lg,
                border: `1px solid ${colors.slate[200]}`,
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.text.secondary }}
                  >
                    Gift Card ID
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    #{withdrawTarget.giftCardId} (
                    {withdrawTarget.tokenName || "AutoBET Pass"})
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.text.secondary }}
                  >
                    Locked Value
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, color: colors.primary.main }}
                  >
                    {safeFromWei(withdrawTarget.BETValue)} BET
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.text.secondary }}
                  >
                    Creator Recipient
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                  >
                    {withdrawTarget.creator}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              This transaction will execute an on-chain call to{" "}
              <code>
                giftcardcinstance.withdrawCreatorTokens(
                {withdrawTarget.giftCardId})
              </code>
              . Please confirm the transaction in your connected Web3 wallet.
            </Typography>
          </Box>
        )}
      </CommonDialog>
    </DashboardLayout>
  );
};

// Helper for logo resolution
const getImageByLogoHash = (hash, logos) => {
  return getImageUrlByHash(hash, logos) || AutobetLogo;
};

GiftCardListPage.propTypes = {
  role: PropTypes.oneOf(["admin", "creator"]),
};

export default GiftCardListPage;
